import { FC } from 'react'

import classNames from 'classnames'
import { capitalize } from 'lodash'

import { staking } from '@banx/api/common'
import {
  BANX_TOKEN_DECIMALS,
  TOTAL_BANX_NFTS,
  TOTAL_BANX_NFTS_PARTNER_POINTS,
} from '@banx/constants'
import {
  banxTokenBNToFixed,
  calcPartnerPoints,
  getAdventureStatus,
  useBanxTokenCirculatingAmount,
} from '@banx/pages/common/AdventuresPage'
import { bnToHuman, formatCompact, formatNumbersWithCommas } from '@banx/utils'

import styles from './AdventuresContent.module.less'

type TotalParticipationColumnProps = {
  adventure: staking.BanxAdventure
}

export const TotalParticipationColumn: FC<TotalParticipationColumnProps> = ({ adventure }) => {
  const { amount: circulatingBanxAmount } = useBanxTokenCirculatingAmount()

  const { tokensPerPoints, totalTokensStaked, totalBanxSubscribed, totalPartnerPoints } = adventure

  const circulatingBanxFloat = bnToHuman(circulatingBanxAmount, BANX_TOKEN_DECIMALS)
  const tokensPerPointsFloat = bnToHuman(tokensPerPoints, BANX_TOKEN_DECIMALS)

  //? Calculate total subscribed partner points
  const subscribedTokensPartnerPoints = calcPartnerPoints(totalTokensStaked, tokensPerPoints)
  const subscribedNftsPartnerPoints = totalPartnerPoints.toNumber()
  const totalSubscribedPartnerPoints = subscribedTokensPartnerPoints + subscribedNftsPartnerPoints

  //? Calculate maximum possible partner points
  const maxPartnerPoints =
    tokensPerPointsFloat === 0
      ? TOTAL_BANX_NFTS_PARTNER_POINTS
      : circulatingBanxFloat / tokensPerPointsFloat + TOTAL_BANX_NFTS_PARTNER_POINTS

  //? Formatting values
  const formattedNftsSubscribed = `${formatNumbersWithCommas(
    totalBanxSubscribed.toNumber(),
  )}/${formatNumbersWithCommas(TOTAL_BANX_NFTS)}`

  const formattedTokensSubscribed = `${formatCompact(
    banxTokenBNToFixed(totalTokensStaked, 0),
  )}/${formatCompact(banxTokenBNToFixed(circulatingBanxAmount, 0))}`

  const formattedPartnerPoints = `${formatCompact(
    totalSubscribedPartnerPoints.toFixed(0),
  )}/${formatCompact(maxPartnerPoints.toFixed(0))}`

  return (
    <div className={styles.statsCol}>
      <h5>Total Participation</h5>
      <p>{formattedNftsSubscribed} Banx</p>
      <p>{formattedTokensSubscribed} $BANX</p>
      <p>{formattedPartnerPoints} Partner pts</p>
    </div>
  )
}

export const AdventureCardHeader: FC<{ adventure: staking.BanxAdventure }> = ({ adventure }) => {
  const adventureStatus = getAdventureStatus(adventure)
  const isEnded = adventureStatus === staking.AdventureStatus.ENDED

  const currentWeek = adventure.week.toNumber()

  return (
    <div className={styles.header}>
      <h3 className={classNames(styles.title, { [styles.titleEnded]: isEnded })}>
        Week {currentWeek}
      </h3>
      <p className={classNames(styles.status, styles[`status__${adventureStatus}`])}>
        {capitalize(adventureStatus)}
      </p>
    </div>
  )
}
